import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  CommentRequest,
  ResponseInterfaceWithAction,
  Task,
  TaskAdditionalParamsInterface,
  TaskAssignedRoles,
  Comment,
  TaskCoverage,
  TaskDetails,
  TaskFormInterface,
  TaskItemInterface,
  TasksBySecurityDomain,
  TasksResponseInterface,
  SeverityEnum,
  StatusDto,
  UpdateTaskStatusRequest,
  TaskStatusEnum,
} from '../interfaces';
import { map, Observable, Subject, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { generateTitle } from '../handlers';
import { calculateImplementationTimeline, formatSeverity } from '../helpers';
import { BusinessClockService } from './business-clock.service';

function normalizeAssignedRoles<T extends { id: string; name: string }>(
  roles: T | T[] | null | undefined,
): T[] {
  if (!roles) {
    return [];
  }
  return (Array.isArray(roles) ? roles : [roles]).filter(Boolean);
}

function normalizeTaskAssignedRoles(roles: TaskAssignedRoles | undefined): TaskAssignedRoles {
  return {
    owner: normalizeAssignedRoles(roles?.owner),
    accountables: normalizeAssignedRoles(roles?.accountables),
    consulteds: normalizeAssignedRoles(roles?.consulteds),
    informeds: normalizeAssignedRoles(roles?.informeds),
  };
}

function getAllDepartmentsFromRoles(
  departments: TaskAssignedRoles,
): { id: string; name: string }[] {
  return [
    ...normalizeAssignedRoles(departments.owner),
    ...normalizeAssignedRoles(departments.accountables),
    ...normalizeAssignedRoles(departments.consulteds),
    ...normalizeAssignedRoles(departments.informeds),
  ].filter((dept) => dept?.id);
}

/** Adds `totalSeverity`: the severity most of the domain's tasks have, the more severe one on a tie. */
function withTotalSeverity(domain: TasksBySecurityDomain): TasksBySecurityDomain {
  let highestSeverity = SeverityEnum.low;
  let highestCount = 0;

  if (domain.severityForDomain) {
    if (domain.severityForDomain.critical > highestCount) {
      highestCount = domain.severityForDomain.critical;
      highestSeverity = SeverityEnum.critical;
    }
    if (domain.severityForDomain.high > highestCount) {
      highestCount = domain.severityForDomain.high;
      highestSeverity = SeverityEnum.high;
    }
    if (domain.severityForDomain.medium > highestCount) {
      highestCount = domain.severityForDomain.medium;
      highestSeverity = SeverityEnum.medium;
    }
    if (domain.severityForDomain.low > highestCount) {
      highestSeverity = SeverityEnum.low;
    }
  }

  return { ...domain, totalSeverity: highestSeverity };
}

@Injectable()
export class TasksService {
  public loading = false;

  public tasks: TaskItemInterface[] = [];
  public allTasks: TaskItemInterface[] = [];
  public taskStatus: TaskAdditionalParamsInterface[] = [];
  public taskPriority: TaskAdditionalParamsInterface[] = [];

  public changeTaskStatus$: Subject<{ id: string; prev: number }> = new Subject<{
    id: string;
    prev: number;
  }>();

  constructor(
    private _http: HttpClient,
    private businessClockService: BusinessClockService,
  ) {}

  public getTasks(controlId?: string, homeTasks = false): Observable<Task[]> {
    const url = homeTasks ? 'GetUsersTaskForHomepage' : 'GetUsersTasks';
    return this._http
      .get<Task[]>(`${environment.baseUrl}/CMDISOTasks/${url}`, {
        params: controlId ? { controlId } : {},
      })
      .pipe(
        map((tasks: Task[]) => {
          return tasks.map(this.normalizeTask);
        }),
      );
  }

  normalizeTask = (task: Task) => {
    const departments = normalizeTaskAssignedRoles(task.raci?.departments);
    const users = normalizeTaskAssignedRoles(task.raci?.users);
    const isBlocked = task.taskLocked || task.isBlocked || task.isBlockedByPrerequisites;

    return {
      ...task,
      implementationTimeline: calculateImplementationTimeline(
        task.dueBy,
        this.businessClockService.nowUtc(),
      ),
      raci: {
        ...task.raci,
        departments,
        users,
      },
      allDepartments: getAllDepartmentsFromRoles(departments),
      ownerDepartmentId: departments.owner?.[0]?.id || '',
      severity: formatSeverity(task.priority),
      taskStatusId:
        isBlocked && task.taskStatusId !== TaskStatusEnum.completed
          ? TaskStatusEnum.blocked
          : task.taskStatusId,
    };
  };

  fetchTasksCoverage(): Observable<TaskCoverage> {
    return this._http
      .get<TaskCoverage>(`${environment.baseUrl}/CMDISOTasks/GetActiveTasksCoverage`)
      .pipe(
        map((taskCoverage) => {
          return {
            ...taskCoverage,
            openTasksBySecurityDomain:
              taskCoverage.openTasksBySecurityDomain.map(withTotalSeverity),
            // The sidebar shows these when "Show open tasks only" is off.
            allTasksBySecurityDomain: (taskCoverage.allTasksBySecurityDomain ?? []).map(
              withTotalSeverity,
            ),
          };
        }),
      );
  }

  public updateTaskById(
    requestData: TaskFormInterface,
  ): Observable<TasksResponseInterface<string>> {
    return this._http.put<TasksResponseInterface<string>>(
      `${environment.baseUrl}/CMTasks/updateTask`,
      requestData,
    );
  }

  public getTaskStatuses(): Observable<StatusDto[]> {
    return this._http.get<StatusDto[]>(`${environment.baseUrl}/CMDISOTasks/GetStatuses`);
  }

  public updateTaskStatus(requestData: UpdateTaskStatusRequest): Observable<Task> {
    return this._http
      .put<Task>(`${environment.baseUrl}/CMDISOTasks/SetStatus`, {
        statusId: requestData.status,
        taskId: requestData.taskId,
        outputText: requestData.outputText ?? null,
      })
      .pipe(map(this.normalizeTask));
  }

  public markTasksAsReviewed(controlId?: string): Observable<any> {
    return this._http.post<Task>(
      `${environment.baseUrl}/CMDISOTasks/SetTasksAsReviewed`,
      {},
      { params: controlId ? { controlId } : {} },
    );
  }
  public fetchTaskDetails(taskId: string): Observable<TaskDetails> {
    return this._http.get<TaskDetails>(`${environment.baseUrl}/CMDISOTasks/GetDetailsForSidebar`, {
      params: { taskId },
    });
  }

  addComment(taskId: string, commentText: string): Observable<Comment> {
    return this._http.post<Comment>(`${environment.baseUrl}/CMDISOComments/AddComment`, {
      ObjectName: 'CMDISOTasks',
      Comment: commentText,
      Id1: taskId,
    });
  }

  public deleteTaskByTaskId(taskId: string): Observable<TasksResponseInterface<string>> {
    return this._http.delete<TasksResponseInterface<string>>(
      `${environment.baseUrl}/CMTasks/deleteTaskById/${taskId}`,
    );
  }

  public getTaskById(
    taskId: string,
  ): Observable<TasksResponseInterface<ResponseInterfaceWithAction<TaskItemInterface>>> {
    return this._http.get<TasksResponseInterface<ResponseInterfaceWithAction<TaskItemInterface>>>(
      `${environment.baseUrl}/CMTasks/getTaskById/${taskId}`,
    );
  }

  public createNewTask(requestData: TaskFormInterface): Observable<TasksResponseInterface<string>> {
    return this._http.post<TasksResponseInterface<string>>(
      `${environment.baseUrl}/CMTasks/createTask`,
      requestData,
    );
  }

  public getTaskStatus(): Observable<TasksResponseInterface<TaskAdditionalParamsInterface[]>> {
    return this._http
      .get<
        TasksResponseInterface<TaskAdditionalParamsInterface[]>
      >(`${environment.baseUrl}/CMTasks/getAllTaskStatus`)
      .pipe(
        tap((response) => {
          if (response?.data) response.data = this._getTaskStatusMap(response.data);
        }),
      );
  }

  public getTaskPriority(): Observable<TasksResponseInterface<TaskAdditionalParamsInterface[]>> {
    return this._http.get<TasksResponseInterface<TaskAdditionalParamsInterface[]>>(
      `${environment.baseUrl}/CMTasks/getAllTaskPriority`,
    );
  }

  private _getTaskStatusMap(
    statusArray: TaskAdditionalParamsInterface[],
  ): TaskAdditionalParamsInterface[] {
    statusArray = [
      {
        value: 0,
        label: 'New',
      },
      {
        value: 1,
        label: 'Assigned',
      },
      ...statusArray,
    ];

    return statusArray.map((status) => {
      status.label = generateTitle(status.label);
      return status;
    });
  }

  public addNewCommentForTask(params: CommentRequest) {
    return this._http.patch(`${environment.baseUrl}/CMTasks/addTaskComment`, params);
  }

  public getAllTimeZones(): Observable<{ key: string; value: string }[]> {
    return this._http.get<{ key: string; value: string }[]>(
      `${environment.baseUrl}/CMSetting/getAllTimeZones`,
    );
  }
}
